import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { Button, Icon } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import {
  EmptyState,
  PageHeader,
  PaginationWrapper,
  PaginationWrapperRefProps,
  PopoverFilterEntry,
  TableColumn,
} from '~/components';
import { EventDetailModal } from '~/components/Dialog';
import { RelativeTime } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import {
  EventRepository,
  EventTypes,
  ParsedEvent,
  getEventACHType,
  getEventIcon,
  getEventRoute,
  getEventStatus,
  getEventSummary,
  getRawEventDirection,
  getRawEventType,
  parseEvent,
} from '~/repositories';
import { EventEvent } from '~/typings/API';

type EventsTableRow = ParsedEvent & { action: React.ReactElement };

export const EventMeta = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  margin: -4px 0;
  gap: 4px;
`;

export const EventType = styled.h4`
  flex: auto;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  margin: 0;
  flex-grow: 0;
  padding: 0;
  text-transform: capitalize;
`;

export const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
`;

export const EventIcon = styled.div`
  margin-left: -2px;
  display: flex;
  align-items: center;
`;

export const Action = styled.div`
  text-align: right;
`;

export const JSONButton = styled(Button)`
  &:hover {
    &:after {
      background: ${({ theme }) => theme.gray.blendToBackground(75)};
    }
  }
`;

export const PageEvents: React.FC = () => {
  const navigate = useNavigate();
  const paginationRef = useRef<PaginationWrapperRefProps>(null);
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);

  const handleOpenEventDetail = (data: any) => {
    setSelectedEventData(data);
    setIsEventDetailModalOpen(true);
  };

  const handleFetch = async (params: any) => {
    return EventRepository.getAll(params).then((response) => {
      const entries = response.events.map((event: EventEvent) => ({
        ...parseEvent(event),
        action: (
          <Action>
            <JSONButton
              size="tiny"
              variant="muted"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEventDetail(event.data);
              }}
            >
              JSON
            </JSONButton>
          </Action>
        ),
      }));

      return { entries, hasMore: Boolean(response?.hasMore) };
    });
  };

  const eventTypes = Object.values(EventTypes).filter((name: string) => !['UNRECOGNIZED'].includes(name));

  const filter: PopoverFilterEntry[] = [
    {
      id: 'types',
      label: 'Type',
      type: 'select',
      list: eventTypes.map((eventType) => ({
        label: eventType,
        value: eventType,
      })),
    },
    {
      id: 'created',
      label: 'Created',
      type: 'date',
    },
  ];

  const columns: TableColumn[] = [
    {
      Header: 'Type',
      accessor: 'data',
      width: 'minmax(300px, auto)',
      Cell: (current) => (
        <EventMeta>
          <EventType>
            {getRawEventDirection(current.value.eventType)} {getRawEventType(current.value.eventType)}{' '}
            {getEventACHType(current.value)}
          </EventType>

          <EventDetail>
            <EventIcon>{getEventIcon(current.value.eventType)}</EventIcon>

            <span>{getEventSummary(current.value)}</span>
          </EventDetail>
        </EventMeta>
      ),
    },
    {
      Header: 'Status',
      accessor: 'eventType',
      width: 'minmax(min-content, 100px)',
      Cell: (current) => getEventStatus(current.value),
    },
    {
      Header: 'Created',
      accessor: 'createdAt',
      sortType: 'datetime',
      width: 'min-content',
      Cell: (current) => <RelativeTime timestamp={current.value} />,
    },
    {
      Header: '',
      width: 'min-content',
      accessor: 'action',
    },
  ];

  return (
    <>
      <EventDetailModal
        isOpen={isEventDetailModalOpen}
        onClose={() => setIsEventDetailModalOpen(false)}
        data={selectedEventData}
      />

      <PageHeader text="Events" border={false} />

      <PaginationWrapper
        tableId="events"
        ref={paginationRef}
        fetch={handleFetch}
        columns={columns}
        filter={filter}
        rowClick={({ original }: { original: EventsTableRow }) =>
          navigate(
            getRawEventType(original.eventType) === 'Identity'
              ? `${getEventRoute(original.eventType)}/${(original.data.subType ?? 'undefined').toLowerCase()}/${original.data.id}`
              : getRawEventType(original.eventType) === undefined
                ? ''
                : `${getEventRoute(original.eventType)}/${original.data.id}`
          )
        }
        empty={
          <EmptyState headline="No events found" text="Create your first person or a business entity.">
            <Button onClick={() => navigate(`${ROUTE.ENTITIES}/edit/person`)} size="small" icon={<Icon.User />}>
              Create Entity
            </Button>
          </EmptyState>
        }
      />
    </>
  );
};
